import React, { useState, useEffect } from "react";
import { IPersonFormData } from "../../dtos.d";
import { showToast } from "../../../../../../components/CustomToast";
import { FloatingSelect } from "../../../../../../components/FloatingSelect";
import { OutlinedInput } from "../../../../../../components/OutlinedInput";
import { SolidButton } from "../../../../../../components/SolidButton";
import axios from "axios";
import { FiArrowLeft, FiTool, FiArrowRight } from "react-icons/fi";

import {
  Container,
  SectionTitle,
  StepDescription,
  InputGroup,
  Row,
  FlexInput,
  CheckboxLabel,
  DeliveryCheckbox,
  BumpCheckbox,
  DiscountText,
  OrderBumpBox,
  BumpHeader,
  BumpTitle,
  BumpDescription,
  Footer,
  BackButton,
} from "./styles";

const cepMask = (value: string) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 9);
};

interface StepDeliveryProps {
  personFormData: IPersonFormData;
  setPersonFormData: React.Dispatch<React.SetStateAction<IPersonFormData>>;
  nextStep: () => void;
  prevStep: () => void;
  loading: boolean;
}

export function StepDelivery({
  personFormData,
  setPersonFormData,
  nextStep,
  prevStep,
  loading,
}: StepDeliveryProps) {
  const [errors, setErrors] = useState<any>({});
  const [searchingCep, setSearchingCep] = useState(false);
  const [showAddressFields, setShowAddressFields] = useState(
    !!personFormData.cep && personFormData.cep.length >= 8
  );

  // Initialize defaults if not set
  useEffect(() => {
    setPersonFormData((prev) => {
      if (prev.hasHeatGun === undefined) {
        return { ...prev, hasHeatGun: true, heatGunVoltage: "" };
      }
      return prev;
    });
  }, [setPersonFormData]);

  const handleChange = (field: keyof IPersonFormData, value: any) => {
    setPersonFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev: any) => ({ ...prev, [field]: "" }));
  };

  const handleCepChange = async (value: string) => {
    const maskedCep = cepMask(value);
    handleChange("cep", maskedCep);

    const cleanCep = maskedCep.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      setSearchingCep(true);
      setShowAddressFields(true);
      try {
        const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
        if (response.data && !response.data.erro) {
          setPersonFormData((prev) => ({
            ...prev,
            address: response.data.logradouro,
            district: response.data.bairro,
            city: response.data.localidade,
            state: response.data.uf,
          }));
          setErrors((prev: any) => ({ ...prev, address: "", district: "", city: "", state: "" }));
        } else {
          showToast({ type: "error", message: "CEP não encontrado." });
        }
      } catch (err) {
        showToast({ type: "error", message: "Erro ao buscar CEP." });
      } finally {
        setSearchingCep(false);
      }
    }
  };

  const validateAndProceed = () => {
    const newErrors: any = {};
    const d = personFormData;

    if (!d.cep || d.cep.length < 9) newErrors.cep = "CEP inválido";
    if (!d.address) newErrors.address = "Rua obrigatória";
    if (!d.number?.trim() && !d.noNumber) newErrors.number = "Informe o número ou marque 'Não possui número'";
    if (!d.complement?.trim() && !d.noComplement) newErrors.complement = "Informe o complemento ou marque 'Sem complemento'";
    if (!d.district) newErrors.district = "Bairro obrigatório";
    if (!d.city) newErrors.city = "Cidade obrigatória";
    if (!d.state) newErrors.state = "Estado obrigatório";

    if (d.hasHeatGun && !d.heatGunVoltage) {
      newErrors.heatGunVoltage = "Selecione a voltagem do soprador";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast({ type: "error", message: "Preencha todos os campos obrigatórios do endereço." });
      return;
    }

    nextStep();
  };

  return (
    <Container>
      <OrderBumpBox $selected={personFormData.hasHeatGun} style={{ marginTop: 0, marginBottom: "24px" }}>
        <BumpHeader $selected={personFormData.hasHeatGun}>
          <FiTool />
          <BumpTitle>Soprador Térmico</BumpTitle>
        </BumpHeader>
        
        <BumpDescription>
          O soprador térmico é um equipamento obrigatório para o treinamento prático. Para sua comodidade, ele já vem incluso no seu Kit Fix it 3D.
        </BumpDescription>

        {personFormData.hasHeatGun && (
          <div style={{ marginTop: "8px" }}>
            <FloatingSelect 
              label="Voltagem do soprador"
              value={personFormData.heatGunVoltage} 
              onChange={(e) => handleChange("heatGunVoltage", e.target.value)}
              error={errors.heatGunVoltage}
            >
              <option value="" disabled hidden></option>
              <option value="110v">110V</option>
              <option value="220v">220V</option>
            </FloatingSelect>
          </div>
        )}

        <CheckboxLabel>
          <BumpCheckbox 
            type="checkbox" 
            checked={!personFormData.hasHeatGun}
            onChange={(e) => {
              handleChange("hasHeatGun", !e.target.checked);
              if (e.target.checked) handleChange("heatGunVoltage", "");
            }} 
          />
          <div>
            Já possuo ou comprarei meu próprio soprador térmico.
            {!personFormData.hasHeatGun && <DiscountText>(-R$ 150,00)</DiscountText>}
          </div>
        </CheckboxLabel>
      </OrderBumpBox>

      <SectionTitle style={{ marginTop: 0 }}>Endereço de Entrega</SectionTitle>
      
      <StepDescription>
        Informe o endereço para receber o seu Kit Fix it 3D.
      </StepDescription>

      <InputGroup>
        <Row>
          <FlexInput flex={1}>
            <OutlinedInput
              inputName="cep"
              label={searchingCep ? "Buscando CEP..." : "CEP"}
              handleChange={handleCepChange}
              value={personFormData.cep}
              error={errors.cep}
            />
          </FlexInput>
          
          {showAddressFields && (
            <FlexInput flex={3}>
              <OutlinedInput
                inputName="address"
                label="Rua / Logradouro"
                handleChange={(val) => handleChange("address", val)}
                value={personFormData.address}
                error={errors.address}
              />
            </FlexInput>
          )}
        </Row>

        {showAddressFields && (
          <>
            <Row>
              <FlexInput>
                <OutlinedInput
                  inputName="number"
                  label="Número"
                  handleChange={(val) => handleChange("number", val)}
                  value={personFormData.noNumber ? "S/N" : personFormData.number}
                  error={errors.number}
                  disabled={personFormData.noNumber}
                />
                <CheckboxLabel>
                  <DeliveryCheckbox 
                    type="checkbox" 
                    checked={personFormData.noNumber}
                    onChange={(e) => {
                      handleChange("noNumber", e.target.checked);
                      if (e.target.checked) handleChange("number", "");
                    }} 
                  />
                  Não possui número
                </CheckboxLabel>
              </FlexInput>

              <FlexInput>
                <OutlinedInput
                  inputName="complement"
                  label="Complemento"
                  handleChange={(val) => handleChange("complement", val)}
                  value={personFormData.noComplement ? "Não possui" : personFormData.complement}
                  disabled={personFormData.noComplement}
                  error={errors.complement}
                />
                <CheckboxLabel>
                  <DeliveryCheckbox 
                    type="checkbox" 
                    checked={personFormData.noComplement}
                    onChange={(e) => {
                      handleChange("noComplement", e.target.checked);
                      if (e.target.checked) handleChange("complement", "");
                    }} 
                  />
                  Sem complemento
                </CheckboxLabel>
              </FlexInput>
            </Row>

            <Row>
              <FlexInput flex={2}>
                <OutlinedInput
                  inputName="district"
                  label="Bairro"
                  handleChange={(val) => handleChange("district", val)}
                  value={personFormData.district}
                  error={errors.district}
                />
              </FlexInput>
              <FlexInput flex={2}>
                <OutlinedInput
                  inputName="city"
                  label="Cidade"
                  handleChange={(val) => handleChange("city", val)}
                  value={personFormData.city}
                  error={errors.city}
                />
              </FlexInput>
              <FlexInput flex={1}>
                <OutlinedInput
                  inputName="state"
                  label="UF"
                  handleChange={(val) => handleChange("state", val)}
                  value={personFormData.state}
                  error={errors.state}
                />
              </FlexInput>
            </Row>
          </>
        )}
      </InputGroup>

      <Footer>
        <BackButton onClick={prevStep}>
          <FiArrowLeft /> Voltar
        </BackButton>

        <SolidButton
          text={<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>Avançar <FiArrowRight /></div>}
          fullWidth
          buttonHeight={48}
          onClick={validateAndProceed}
          loading={loading}
          disabled={loading}
        />
      </Footer>
    </Container>
  );
}